import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Inviter, Registerer, SessionState, UserAgent } from "sip.js";
import { useLocation } from "react-router-dom";

import { useAuthSelector } from "../../services/selectors/authSelector";
import { authOTPVerified, authToken } from "../../storage/authToken";
import { startDriverClinicCall } from "../../services/apis/dispense/clinicCalls";
import {
	playRingtone,
	stopRingtone,
} from "../video-calling/screenWakeLock";
import { subscribeToDriverClinicCallSocketUpdates } from "../../services/realtime/realtimeSocket";
import * as Icons from "../../assets/svg/icons";
import * as routeNames from "../../routes/routeName";

const ClinicCallContext = createContext(null);

const INITIAL_CALL = {
	status: "idle",
	callRef: null,
	appointmentId: null,
	clinicName: "",
	patientName: "",
	error: "",
	backendStatus: null,
	disposition: "",
	durationSeconds: null,
	isMuted: false,
};

const ACTIVE_STATUSES = new Set(["preparing", "registering", "calling", "ringing", "connected"]);
const TERMINAL_BACKEND_STATUSES = new Set([
	"ended",
	"failed",
	"cancelled",
	"canceled",
	"busy",
	"no_answer",
	"completed",
]);

const getValue = (object, keys) => {
	for (const key of keys) {
		const value = object?.[key];
		if (value != null && String(value).trim() !== "") return value;
	}
	return null;
};

const normalizeWsServer = (sip) => {
	const value = getValue(sip, [
		"wss_url",
		"wssUrl",
		"websocket_url",
		"websocketUrl",
		"ws_url",
		"wsUrl",
		"websocket",
		"server",
	]);
	const nested = getValue(sip?.transport, ["websocket_url", "ws_url", "server"]);
	return value || nested;
};

const normalizeSipCredentials = (sip) => ({
	username: getValue(sip, [
		"username",
		"user",
		"extension",
		"authorization_username",
		"authorizationUsername",
		"auth_username",
		"authUsername",
	]),
	password: getValue(sip, ["password", "secret", "authorization_password", "authorizationPassword"]),
	domain: getValue(sip, ["domain", "host"]),
	wssUrl: normalizeWsServer(sip),
});

const normalizeDialTo = (dial) => getValue(dial, ["to", "phone", "phone_number", "phoneNumber", "destination"]);

const buildSipUri = (value) => UserAgent.makeURI(String(value || "").trim());

const createSetupError = (field) => new Error(`Clinic call setup is missing ${field}. Please contact support.`);

const normalizeIceServers = (iceServers) => {
	if (!Array.isArray(iceServers)) return [];

	return iceServers
		.map((server) => {
			if (typeof server === "string") return { urls: server };
			if (!server || typeof server !== "object") return null;
			if (server.urls || server.url) {
				return {
					...server,
					urls: server.urls || server.url,
				};
			}
			return null;
		})
		.filter(Boolean);
};

const getApiErrorMessage = (error, fallback = "Unable to start clinic call.") => {
	const data = error?.response?.data;
	const validationError = data?.data?.error;
	if (validationError && typeof validationError === "object") {
		const firstValue = Object.values(validationError)[0];
		if (Array.isArray(firstValue) && firstValue[0]) return firstValue[0];
		if (typeof firstValue === "string") return firstValue;
	}

	return data?.message || error?.message || fallback;
};

const isMicPermissionError = (error) => (
	["NotAllowedError", "PermissionDeniedError", "NotFoundError", "NotReadableError"].includes(error?.name)
);

const requestMicrophoneAccess = async () => {
	if (!navigator.mediaDevices?.getUserMedia) {
		throw new Error("Microphone access is not available in this browser.");
	}

	const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
	stream.getTracks().forEach((track) => track.stop());
};

const mapBackendStatus = (status) => {
	const normalized = String(status || "").toLowerCase();
	if (["answered", "accepted", "human_answered"].includes(normalized)) return "connected";
	if (["ringing", "ready", "requested", "initiated", "queued", "dialing", "connecting", "connected"].includes(normalized)) return "ringing";
	if (TERMINAL_BACKEND_STATUSES.has(normalized)) return "ended";
	return null;
};

const mapRealtimeStatus = (payload) => {
	const normalized = String(payload?.webhook_event || payload?.status || "").toLowerCase();
	if (["call.ringing", "ringing"].includes(normalized)) return "ringing";
	if (["call.answered", "answered"].includes(normalized)) return "connected";
	if (["call.ended", "ended"].includes(normalized)) return "ended";
	return mapBackendStatus(normalized);
};

const statusLabel = (status) => {
	switch (status) {
		case "preparing":
			return "Preparing";
		case "registering":
			return "Registering";
		case "calling":
			return "Calling";
		case "ringing":
			return "Ringing";
		case "connected":
			return "Connected";
		case "dialing":
			return "Dialing";
		case "ended":
			return "Call ended";
		case "failed":
			return "Call failed";
		default:
			return "Clinic call";
	}
};

const centerStatusLabel = (status) => {
	switch (status) {
		case "preparing":
			return "Preparing clinic call";
		case "registering":
			return "Registering call line";
		case "calling":
			return "Calling clinic";
		case "connected":
			return "Clinic connected";
		case "ringing":
			return "Clinic ringing";
		case "dialing":
			return "Opening your phone to call the clinic";
		case "ended":
			return "Call ended";
		case "failed":
			return "Clinic call failed";
		default:
			return "Connecting clinic please wait";
	}
};

const buildTelHref = (phone) => {
	// Keep only characters a dialer understands (+ for country code, * / # for
	// IVR extensions). Everything else — spaces, dashes, parentheses — is noise.
	const cleaned = String(phone || "").trim().replace(/[^\d+*#]/g, "");
	return cleaned ? `tel:${cleaned}` : "";
};

// Hands the call off to the device's native phone dialer. Inside the Flutter
// WebView wrapper a `tel:` navigation is intercepted by `_handleExternalScheme`
// and opens the system dialer; in a plain mobile browser it opens the OS phone
// handler. Returns true when a dial was actually triggered.
const placeNativePhoneCall = (phone) => {
	const href = buildTelHref(phone);
	if (!href || typeof document === "undefined") return false;

	try {
		const anchor = document.createElement("a");
		anchor.href = href;
		anchor.rel = "noopener";
		anchor.style.display = "none";
		document.body.appendChild(anchor);
		anchor.click();
		document.body.removeChild(anchor);
		return true;
	} catch (error) {
		try {
			window.location.href = href;
			return true;
		} catch (fallbackError) {
			return false;
		}
	}
};

// True when the web app is running inside the Flutter native shell. Mirrors the
// detection used by the video-call handlers so both call types agree on when to
// hand a call off to native.
const isFlutterNativeShell = () => {
	try {
		const query = window.location?.search || "";
		return Boolean(
			window.__FLUTTER_NATIVE_VIDEO_CALLS__ ||
			query.includes("flutter_app=1") ||
			window.sessionStorage?.getItem("flutter_app") === "1"
		);
	} catch (error) {
		return false;
	}
};

// Sends the minted SIP line to the native app, which opens its own call screen
// (earpiece by default, with mute/speaker/keypad). Returns false when the
// bridge isn't available so the caller can fall back.
const postClinicCallToNative = (payload) => {
	try {
		if (!window.DriverApp || typeof window.DriverApp.postMessage !== "function") {
			return false;
		}
		window.DriverApp.postMessage(JSON.stringify(payload));
		return true;
	} catch (error) {
		return false;
	}
};

const getPatientLabel = (name) => String(name || "").trim().toUpperCase();

const getCallDateTime = () => {
	const now = new Date();
	return {
		date: now.toLocaleDateString([], {
			month: "short",
			day: "numeric",
			year: "numeric",
		}),
		time: now.toLocaleTimeString([], {
			hour: "numeric",
			minute: "2-digit",
			second: "2-digit",
		}),
	};
};

export const ClinicCallProvider = ({ children }) => {
	const { pharmacyId } = useAuthSelector();
	const location = useLocation();
	const [call, setCall] = useState(INITIAL_CALL);
	const [callDateTime, setCallDateTime] = useState(getCallDateTime);
	const audioRef = useRef(null);
	const userAgentRef = useRef(null);
	const registererRef = useRef(null);
	const inviterRef = useRef(null);
	const cleanupTimerRef = useRef(null);
	const callRefRef = useRef(null);
	const micMutedRef = useRef(false);

	const clearRemoteAudio = useCallback(() => {
		if (audioRef.current) {
			audioRef.current.srcObject = null;
		}
	}, []);

	const attachRemoteAudio = useCallback((inviter) => {
		const peerConnection = inviter?.sessionDescriptionHandler?.peerConnection;
		if (!peerConnection || !audioRef.current) return;

		const remoteStream = new MediaStream();
		peerConnection.getReceivers().forEach((receiver) => {
			if (receiver.track) {
				remoteStream.addTrack(receiver.track);
			}
		});

		audioRef.current.srcObject = remoteStream;
		audioRef.current.play?.().catch(() => {});
	}, []);

	const applyMicrophoneMuted = useCallback((muted) => {
		const sessionDescriptionHandler = inviterRef.current?.sessionDescriptionHandler;
		const peerConnection = sessionDescriptionHandler?.peerConnection;
		let applied = false;

		peerConnection?.getSenders?.().forEach((sender) => {
			if (sender.track?.kind === "audio") {
				sender.track.enabled = !muted;
				applied = true;
			}
		});

		[
			sessionDescriptionHandler?.localMediaStream,
			sessionDescriptionHandler?._localMediaStream,
		].forEach((stream) => {
			stream?.getAudioTracks?.().forEach((track) => {
				track.enabled = !muted;
				applied = true;
			});
		});

		return applied;
	}, []);

	const disconnectSip = useCallback(async ({ hangup = false } = {}) => {
		const inviter = inviterRef.current;
		const registerer = registererRef.current;
		const userAgent = userAgentRef.current;
		inviterRef.current = null;
		registererRef.current = null;
		userAgentRef.current = null;
		clearRemoteAudio();

		try {
			if (hangup && inviter && inviter.state !== SessionState.Terminated) {
				if (inviter.state === SessionState.Established) {
					await inviter.bye();
				} else {
					await inviter.cancel();
				}
			}
		} catch (error) {
			// The session may already be closed by the provider or remote side.
		}

		try {
			await registerer?.unregister();
		} catch (error) {}

		try {
			await userAgent?.stop();
		} catch (error) {}
	}, [clearRemoteAudio]);

	const finishCall = useCallback((nextStatus = "ended", message = "") => {
		callRefRef.current = null;
		micMutedRef.current = false;
		stopRingtone();

		if (cleanupTimerRef.current) {
			window.clearTimeout(cleanupTimerRef.current);
			cleanupTimerRef.current = null;
		}

		if (nextStatus === "ended") {
			setCall(INITIAL_CALL);
			return;
		}

		setCall((previous) => ({
			...previous,
			status: nextStatus,
			error: message || previous.error,
		}));

		cleanupTimerRef.current = window.setTimeout(() => {
			setCall((previous) => (
				ACTIVE_STATUSES.has(previous.status)
					? previous
					: INITIAL_CALL
			));
		}, 7000);
	}, []);

	const applyRealtimeCallUpdate = useCallback((eventData) => {
		const activeCallRef = callRefRef.current;
		if (!activeCallRef || String(eventData?.call_ref || "") !== String(activeCallRef)) return;

		const mappedStatus = mapRealtimeStatus(eventData);
		if (!mappedStatus) return;

		setCall((previous) => ({
			...previous,
			backendStatus: eventData?.status || previous.backendStatus,
			disposition: eventData?.disposition || previous.disposition,
			durationSeconds: eventData?.duration_seconds ?? previous.durationSeconds,
			status: previous.status === "failed" ? previous.status : mappedStatus,
		}));

		if (mappedStatus === "ended") {
			disconnectSip({ hangup: false });
			finishCall("ended");
		}
	}, [disconnectSip, finishCall]);

	const startClinicCall = useCallback(async ({
		pharmacyId,
		appointmentId,
		pharmacyPatientId,
		manualToPhone,
		fallbackPhone,
		clinicName,
		patientName,
	}) => {
		// When the masked SIP line can't be established (provider misconfigured,
		// unreachable, or the WebView blocks the mic), fall back to a plain
		// cellular call so the driver is never blocked from reaching the clinic.
		const failWithFallback = (message) => {
			if (placeNativePhoneCall(fallbackPhone)) {
				stopRingtone();
				callRefRef.current = null;
				micMutedRef.current = false;
				if (cleanupTimerRef.current) {
					window.clearTimeout(cleanupTimerRef.current);
					cleanupTimerRef.current = null;
				}
				setCall((previous) => ({
					...INITIAL_CALL,
					status: "dialing",
					clinicName: previous.clinicName || clinicName || "Clinic",
				}));
				cleanupTimerRef.current = window.setTimeout(() => {
					setCall((previous) => (previous.status === "dialing" ? INITIAL_CALL : previous));
				}, 3500);
				return;
			}

			setCall((previous) => ({
				...previous,
				status: "failed",
				error: message,
			}));
			finishCall("failed", message);
		};

		if (ACTIVE_STATUSES.has(call.status)) {
			setCall((previous) => ({
				...previous,
				error: "End the active clinic call before starting another one.",
			}));
			return;
		}

		if (!pharmacyId || !appointmentId) {
			setCall({
				...INITIAL_CALL,
				status: "failed",
				error: "Missing appointment details for clinic call.",
			});
			return;
		}

		if (cleanupTimerRef.current) {
			window.clearTimeout(cleanupTimerRef.current);
			cleanupTimerRef.current = null;
		}

		await disconnectSip({ hangup: false });

		// Inside the Flutter native app, hand off immediately so the native call
		// screen opens instantly — it mints the SIP line itself and does the
		// audio (proper earpiece/speaker/keypad the WebView can't provide). We
		// never render the web call overlay in native mode. The in-page sip.js
		// path below is only used in a plain browser.
		if (isFlutterNativeShell()) {
			const posted = postClinicCallToNative({
				type: "clinicCallStartNative",
				pharmacyId,
				appointmentId,
				pharmacyPatientId: pharmacyPatientId ?? null,
				manualToPhone: manualToPhone || "",
				fallbackPhone: fallbackPhone || "",
				clinicName: clinicName || "Clinic",
				patientName: patientName || "Patient",
				authToken: authToken() || "",
				apiBaseUrl: process.env.REACT_APP_BASE_URL || "",
			});

			if (posted) {
				// Native screen owns the call; keep the web overlay idle.
				setCall(INITIAL_CALL);
				return;
			}

			// Bridge unavailable — try the OS dialer, else fall through to sip.js.
			if (placeNativePhoneCall(fallbackPhone)) {
				setCall(INITIAL_CALL);
				return;
			}
		}

		setCall({
			...INITIAL_CALL,
			status: "preparing",
			appointmentId,
			clinicName: clinicName || "Clinic",
			patientName: patientName || "Patient",
		});

		try {
			await requestMicrophoneAccess();
		} catch (error) {
			const message = isMicPermissionError(error)
				? "Microphone permission is required to call the clinic."
				: error?.message || "Unable to access the microphone.";
			// A native cellular call doesn't need the WebView mic, so hand off
			// to the dialer instead of dead-ending on a permission error.
			failWithFallback(message);
			return;
		}

		try {
			const response = await startDriverClinicCall({
				pharmacyId,
				appointmentId,
				pharmacyPatientId,
				manualToPhone,
			});

			const callRef = response?.call_ref;
			const sip = response?.sip || {};
			const dial = response?.dial || {};
			const credentials = normalizeSipCredentials(sip);
			const dialTo = normalizeDialTo(dial);

			if (!callRef) throw createSetupError("call reference");
			if (!dialTo) throw createSetupError("dial.to");
			if (!credentials.domain) throw createSetupError("sip.domain");
			if (!credentials.username) throw createSetupError("sip.username");
			if (!credentials.password) throw createSetupError("sip.password");
			if (!credentials.wssUrl) throw createSetupError("sip.wss_url");

			const aor = buildSipUri(`sip:${credentials.username}@${credentials.domain}`);
			const target = buildSipUri(`sip:${dialTo}@${credentials.domain}`);

			if (!aor) throw createSetupError("a valid SIP username/domain");
			if (!target) throw createSetupError("a valid SIP dial target");

			callRefRef.current = callRef;

			const userAgent = new UserAgent({
				uri: aor,
				authorizationUsername: String(credentials.username),
				authorizationPassword: String(credentials.password),
				displayName: "OatRx Driver",
				transportOptions: {
					server: String(credentials.wssUrl),
				},
				sessionDescriptionHandlerFactoryOptions: {
					peerConnectionConfiguration: {
						iceServers: normalizeIceServers(response?.ice_servers || response?.iceServers),
					},
				},
			});
			const registerer = new Registerer(userAgent);
			const inviter = new Inviter(userAgent, target, {
				sessionDescriptionHandlerOptions: {
					constraints: {
						audio: true,
						video: false,
					},
				},
			});

			inviter.stateChange.addListener((state) => {
				if (state === SessionState.Establishing) {
					setCall((previous) => ({
						...previous,
						status: previous.status === "failed" ? previous.status : "ringing",
					}));
				}

				if (state === SessionState.Established) {
					attachRemoteAudio(inviter);
					applyMicrophoneMuted(micMutedRef.current);
					setCall((previous) => ({
						...previous,
						status: previous.status === "failed" ? previous.status : "connected",
					}));
				}

				if (state === SessionState.Terminated) {
					if (!callRefRef.current) return;
					disconnectSip({ hangup: false });
					finishCall("ended");
				}
			});

			userAgent.transport.stateChange.addListener((state) => {
				if (String(state) === "Disconnected" && callRefRef.current) {
					disconnectSip({ hangup: false });
					finishCall("failed", "Clinic call connection disconnected.");
				}
			});

			userAgentRef.current = userAgent;
			registererRef.current = registerer;
			inviterRef.current = inviter;
			setCall((previous) => ({
				...previous,
				callRef,
				status: "preparing",
				backendStatus: response?.status || "ready",
			}));

			await userAgent.start();
			setCall((previous) => ({
				...previous,
				status: previous.status === "failed" ? previous.status : "registering",
			}));
			await registerer.register();
			setCall((previous) => ({
				...previous,
				status: previous.status === "failed" ? previous.status : "calling",
			}));
			await inviter.invite({
				requestDelegate: {
					onProgress: () => {
						setCall((previous) => ({
							...previous,
							status: previous.status === "failed" ? previous.status : "ringing",
						}));
					},
				},
			});
		} catch (error) {
			await disconnectSip({ hangup: false });
			const message = getApiErrorMessage(error);
			// SIP line unavailable (missing dial.to / provider rejected /
			// unreachable) — dial the clinic directly on the device instead.
			failWithFallback(message);
		}
	}, [applyMicrophoneMuted, attachRemoteAudio, call.status, disconnectSip, finishCall]);

	const hangUp = useCallback(async () => {
		stopRingtone();
		await disconnectSip({ hangup: true });
		finishCall("ended");
	}, [disconnectSip, finishCall]);

	const toggleMicrophone = useCallback(() => {
		const nextMuted = !micMutedRef.current;
		micMutedRef.current = nextMuted;
		applyMicrophoneMuted(nextMuted);
		setCall((previous) => ({
			...previous,
			isMuted: nextMuted,
		}));
	}, [applyMicrophoneMuted]);

	useEffect(() => {
		const isOtpVerified = Number(authOTPVerified()) === 1;
		const isSwitchPharmacyScreen = location.pathname === routeNames.SWITCH_PHARMACY;
		const isOtpVerificationScreen = location.pathname === routeNames.verify_otp;

		if (!pharmacyId || !call.callRef || !isOtpVerified || isSwitchPharmacyScreen || isOtpVerificationScreen) {
			return undefined;
		}

		let subscription = null;
		try {
			subscription = subscribeToDriverClinicCallSocketUpdates({
				pharmacyId,
				onEvent: applyRealtimeCallUpdate,
			});
		} catch (error) {
			// Without realtime, provider state only has local SIP.js events during testing/current flow.
		}

		return () => {
			subscription?.close?.();
		};
	}, [applyRealtimeCallUpdate, call.callRef, location.pathname, pharmacyId]);

	useEffect(() => () => {
		if (cleanupTimerRef.current) {
			window.clearTimeout(cleanupTimerRef.current);
		}
		stopRingtone();
		disconnectSip({ hangup: false });
	}, [disconnectSip]);

	useEffect(() => {
		if (call.status === "ringing") {
			playRingtone();
			return () => stopRingtone();
		}

		stopRingtone();
		return undefined;
	}, [call.status]);

	useEffect(() => {
		if (call.status === "idle") return undefined;

		setCallDateTime(getCallDateTime());
		const timer = window.setInterval(() => {
			setCallDateTime(getCallDateTime());
		}, 1000);

		return () => window.clearInterval(timer);
	}, [call.status]);

	const value = useMemo(() => ({
		call,
		isCallActive: ACTIVE_STATUSES.has(call.status),
		startClinicCall,
		hangUp,
		toggleMicrophone,
	}), [call, hangUp, startClinicCall, toggleMicrophone]);

	const showBar = call.status !== "idle";

	return (
		<ClinicCallContext.Provider value={value}>
			{children}
			<audio ref={audioRef} autoPlay playsInline className="hidden" />
			{showBar ? (
				<div className="fixed inset-0 z-[21000] flex justify-center bg-[#0f0f12]">
					<div
						className="relative flex h-full w-full flex-col overflow-hidden xl:w-3/12 lg:w-4/12 md:w-5/12 sm:w-6/12"
						style={{
							backgroundColor: "#0f0f12",
							border: "1px solid rgba(255, 255, 255, 0.08)",
							boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
						}}
					>
						<div className="absolute left-5 top-5 z-20 max-w-[72%]">
							<p className="truncate text-lg font-bold leading-tight text-white">
								{getPatientLabel(call.patientName) || "CLINIC CALL"}
							</p>
							<p className="mt-1 flex items-center gap-2 text-sm text-gray-400">
								<span>{callDateTime.date}</span>
								<span className="font-medium text-emerald-500">{callDateTime.time}</span>
							</p>
							{ACTIVE_STATUSES.has(call.status) ? (
								<div className="mt-3 inline-flex max-w-full items-center gap-2 rounded-full border border-blue-400/25 bg-blue-500/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-blue-100">
									<span className="inline-flex gap-1">
										<span className="h-1.5 w-1.5 rounded-full bg-blue-300 animate-bounce" style={{ animationDelay: "0ms" }} />
										<span className="h-1.5 w-1.5 rounded-full bg-blue-300 animate-bounce" style={{ animationDelay: "120ms" }} />
										<span className="h-1.5 w-1.5 rounded-full bg-blue-300 animate-bounce" style={{ animationDelay: "240ms" }} />
									</span>
									<span className="whitespace-normal break-words">{statusLabel(call.status)} {call.clinicName || "Clinic"}</span>
								</div>
							) : null}
						</div>

						<div className="flex flex-1 items-center justify-center px-9">
							<div className="w-full rounded-[24px] border border-white/10 bg-slate-950/70 px-5 py-4 text-center text-white shadow-[0_18px_40px_rgba(0,0,0,0.35)] backdrop-blur-md">
								<div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600 text-lg font-bold text-white">
									<Icons.PhoneIcon color="#ffffff" />
								</div>
								<p className={`text-[11px] font-semibold uppercase tracking-[0.22em] ${call.status === "failed" ? "text-red-300" : "text-blue-200"}`}>
									{centerStatusLabel(call.status)}
								</p>
								{call.status === "failed" && call.error ? (
									<p className="mt-2 text-sm font-medium leading-snug text-slate-200">{call.error}</p>
								) : null}
							</div>
						</div>

						<div className="absolute bottom-5 left-4 right-4 flex justify-center">
							<div
								className="flex items-center justify-center gap-1 rounded-full border px-4 py-2"
								style={{
									backgroundColor: "rgba(39, 39, 42, 0.95)",
									borderColor: "rgba(255, 255, 255, 0.08)",
									boxShadow: "0 4px 24px rgba(0, 0, 0, 0.4)",
								}}
							>
								<button
									type="button"
									onClick={toggleMicrophone}
									className="flex h-10 w-10 items-center justify-center rounded-full text-zinc-200"
									style={call.isMuted ? { backgroundColor: "rgba(239, 68, 68, 0.2)", color: "#ef4444" } : undefined}
									aria-label={call.isMuted ? "Unmute microphone" : "Mute microphone"}
								>
									{call.isMuted ? (
										<Icons.MicOffIcon className="h-5 w-5" color="currentColor" />
									) : (
										<Icons.MicIcon className="h-5 w-5" color="currentColor" />
									)}
								</button>
								{ACTIVE_STATUSES.has(call.status) ? (
									<button
										type="button"
										onClick={hangUp}
										className="flex h-10 w-10 items-center justify-center rounded-full text-red-400"
										aria-label="End call"
									>
										<Icons.CallEndedIcon className="h-5 w-5" color="currentColor" />
									</button>
								) : (
									<button
										type="button"
										onClick={() => setCall(INITIAL_CALL)}
										className="h-10 rounded-full bg-slate-700 px-4 text-sm font-bold text-white"
									>
										Dismiss
									</button>
								)}
							</div>
						</div>
					</div>
				</div>
			) : null}
		</ClinicCallContext.Provider>
	);
};

export const useClinicCall = () => {
	const context = useContext(ClinicCallContext);
	if (!context) {
		throw new Error("useClinicCall must be used within ClinicCallProvider");
	}
	return context;
};
